// pages/StyledDelivery.js
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";
import "../styles/StyledDelivery.css";

const API = process.env.REACT_APP_API_BASE_URL; 

export default function StyledDelivery() {
  const [basket, setBasket] = useState([]);
  const [deliveryMode, setDeliveryMode] = useState("Standard");
  const [contactless, setContactless] = useState(true);
  const [scheduled, setScheduled] = useState("");
  const [note, setNote] = useState("");

  const [products, setProducts] = useState([]); // <-- fetch from backend
  const [loading, setLoading] = useState(true);

  const { addToCart } = useCart();

  // Load persisted basket
  useEffect(() => {
    try {
      const saved = localStorage.getItem("delivery_basket_v1");
      if (saved) setBasket(JSON.parse(saved));
    } catch (e) {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("delivery_basket_v1", JSON.stringify(basket));
    } catch (e) {}
  }, [basket]);

  // Fetch products from backend
  useEffect(() => {
    fetchProducts();
  }, []);

async function fetchProducts() {
  setLoading(true);
  try {
    const res = await fetch(`${API}/products/sales/list`);
    const data = await res.json();

    // force into array shape
    if (Array.isArray(data)) {
      setProducts(data);
    } else if (Array.isArray(data.products)) {
      setProducts(data.products);
    } else {
      console.error("Unexpected response:", data);
      setProducts([]);
    }
  } catch (err) {
    console.error(err);
    alert("Failed to fetch products.");
    setProducts([]);
  } finally {
    setLoading(false);
  }
}


  function addItem(item) {
    setBasket((prev) => {
      const found = prev.find((p) => p._id === item._id);
      if (found) {
        return prev.map((p) =>
          p._id === item._id ? { ...p, qty: p.qty + 1 } : p
        );
      }
      return [{ ...item, qty: 1 }, ...prev];
    });
  }

  function removeItem(id) {
    setBasket((prev) => prev.filter((p) => p._id !== id));
  }

  function updateQty(id, qty) {
    if (qty <= 0) return removeItem(id);
    setBasket((prev) =>
      prev.map((p) => (p._id === id ? { ...p, qty } : p))
    );
  }

  function clearBasket() {
    setBasket([]);
  }

function addAllToCart() {
  if (addToCart && typeof addToCart === "function") {
    basket.forEach((item) => {
      // pass productId, qty, and unitPrice (fallback to item's price if salePrice missing)
      const unitPrice = item.salePrice != null ? item.salePrice : item.price || item.salePrice;
      // ensure numeric quantity
      const qty = Number(item.qty) || 1;
      addToCart(item._id, qty, unitPrice);
    });
    setBasket([]);
  } else {
    console.log("Add to cart (simulated):", basket);
  }
}


  const subtotal = basket
    .reduce((s, p) => s + p.salePrice * p.qty, 0)
    .toFixed(2);
  const estimatedFee = deliveryMode === "Express" ? 9.5 : 4.0;
  const total = (parseFloat(subtotal) + estimatedFee).toFixed(2);

  return (
    <section className="delivery-root">
      {/* LEFT SIDE - Delivery Options */}
      <motion.div
        className="delivery-left"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="dl-title">Delivery Hub — Fresh to your Door</h2>
        <p className="dl-sub">
          Choose how you want it delivered. Schedule a time, toggle contactless
          and add notes for the vendor.
        </p>

        {/* Delivery Mode */}
        <div className="mode-row">
          <motion.button
            whileTap={{ scale: 0.98 }}
            className={`mode-btn ${
              deliveryMode === "Standard" ? "active" : ""
            }`}
            onClick={() => setDeliveryMode("Standard")}
          >
            Standard
            <span className="mode-sub">2–4 days • ${4}</span>
          </motion.button>

          <motion.button
            whileTap={{ scale: 0.98 }}
            className={`mode-btn ${
              deliveryMode === "Express" ? "active" : ""
            }`}
            onClick={() => setDeliveryMode("Express")}
          >
            Express
            <span className="mode-sub">Same day/Next day • ${9.5}</span>
          </motion.button>
        </div>

        {/* Schedule + Contactless */}
        <div className="control-row">
          <label className="control-row-item">
            <span className="control-label">Schedule</span>
            <input
              type="datetime-local"
              value={scheduled}
              onChange={(e) => setScheduled(e.target.value)}
              className="control-input"
            />
          </label>

          <label className="control-row-item toggle">
            <span className="control-label">Contactless</span>
            <input
              type="checkbox"
              checked={contactless}
              onChange={() => setContactless((p) => !p)}
              className="control-checkbox"
            />
          </label>
        </div>

        {/* Delivery Note */}
        <label className="note-area">
          <span className="control-label">Delivery note (optional)</span>
          <textarea
            placeholder="Leave gate code, preferred drop-off spot, or allergy notes..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="note-input"
            rows={3}
          />
        </label>

        {/* Buttons */}
        <div className="left-actions">
          <motion.button
            className="btn btn-primary"
            whileHover={{ scale: 1.03 }}
            onClick={addAllToCart}
            disabled={basket.length === 0}
          >
            Add all to cart • ${total}
          </motion.button>

          <motion.button
            className="btn btn-ghost"
            whileHover={{ scale: 1.03 }}
            onClick={clearBasket}
            disabled={basket.length === 0}
          >
            Clear Basket
          </motion.button>
        </div>
      </motion.div>

      {/* RIGHT SIDE - Basket + Suggested Products */}
      <motion.div
        className="delivery-right"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.05 }}
      >
        {/* Basket */}
        <div className="basket-card">
          <div className="basket-header">
            <h3>Your Delivery Basket</h3>
            <span className="basket-count">{basket.length} items</span>
          </div>

          <div className="basket-body">
            <AnimatePresence>
              {basket.length === 0 && (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  className="empty-state"
                >
                  <img
                    src="/images/h3.png"
                    alt="empty"
                    className="empty-illu"
                  />
                  <p>Your basket is empty. Add fresh items below.</p>
                </motion.div>
              )}

              {basket.map((item) => (
                <motion.div
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  key={item._id}
                  className="basket-item"
                >
                  <img src={item.image} alt={item.name} className="item-img" />
                  <div className="item-meta">
                    <div className="item-name">{item.name}</div>
                    <div className="item-price">
                      ${(item.salePrice * item.qty).toFixed(2)}
                    </div>
                    <div className="qty-controls">
                      <button onClick={() => updateQty(item._id, item.qty - 1)}>
                        -
                      </button>
                      <span>{item.qty}</span>
                      <button onClick={() => updateQty(item._id, item.qty + 1)}>
                        +
                      </button>
                    </div>
                  </div>

                  <button
                    className="remove-btn"
                    onClick={() => removeItem(item._id)}
                    aria-label={`Remove ${item.name}`}
                  >
                    ×
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="basket-footer">
            <div className="subtotal">
              <span>Subtotal</span>
              <strong>${subtotal}</strong>
            </div>
            <div className="fee">
              <span>Delivery ({deliveryMode})</span>
              <span>${estimatedFee.toFixed(2)}</span>
            </div>
            <div className="total">
              <span>Total</span>
              <strong>${total}</strong>
            </div>
          </div>
        </div>

        {/* Suggested Products */}
        <div className="suggestions">
          <h4>Recommended for your delivery</h4>
          {loading ? (
            <div>Loading products…</div>
          ) : (
            <div className="suggest-grid">
              {products.map((it) => (
                <motion.article
                  key={it._id}
                  className="suggest-card"
                  whileHover={{ scale: 1.03 }}
                  layout
                >
                  <img src={it.image} alt={it.name} />
                  <div className="suggest-meta">
                    <div className="suggest-name">{it.name}</div>
                    <div className="suggest-bottom">
                      <strong>${it.salePrice.toFixed(2)}</strong>
                      <button
                        className="add-small"
                        onClick={() => addItem(it)}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </section>
  );
}
